import React, { useState } from 'react'

import {
  Settings as SettingsIcon,
  PlusCircle as PlusCircleIcon,
} from 'react-feather'
import { Box, Button, Grid, TextField, Typography } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'

import { Autocomplete, BaseDialog, LoadingFeedback } from 'components'
import { CompanySupplierAuditUserTable } from './components'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import schema from './schema'
import useStyles from './styles'
import helpers from 'helpers'

const UsersManagementDialog = ({ open, setOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false)

  const classes = useStyles()
  const snackbar = useSnackbar()

  const { control, handleSubmit, errors } = useForm({
    validationSchema: schema,
    defaultValues: {
      email: '',
      companySupplierQuestionnaires: [],
    },
  })

  const {
    response,
    isLoading: isCompanySupplierQuestionnaireUsersLoading,
    refresh,
  } = useFetch(service.supplierPortal.companySupplierQuestionnaireUsers.get)

  const questionnaireUsers = response?.data?.companySupplierQuestionnaireUsers

  const {
    response: questionnairesResponse,
    isLoading: isCompanySupplierQuestionnairesLoading,
    refresh: refreshQuestionnaires,
  } = useFetch(service.supplierPortal.companySupplierQuestionnaires.get, {
    minimal: true,
  })

  const companySupplierQuestionnaires =
    questionnairesResponse?.data?.companySupplierQuestionnaires

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)

      const companySupplierQuestionnaireIds =
        data.companySupplierQuestionnaires.map(
          (companySupplierQuestionnaire) => companySupplierQuestionnaire.id,
        )

      await service.supplierPortal.companySupplierQuestionnaireUsers.create({
        email: data.email,
        companySupplierQuestionnaireIds,
      })

      snackbar.open({
        message: 'Usuário vinculado ao(s) questionário(s) com sucesso.',
        variant: 'success',
      })

      refresh()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao vincular usuário ao(s) questionário(s)',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <BaseDialog
      open={open}
      setOpen={setOpen}
      title={
        <Box display="flex" alignItems="center" flexDirection="row" gridGap={3}>
          <SettingsIcon size={20} />
          <Typography variant="h4">Gestão de Usuários</Typography>
        </Box>
      }
      actionButtonText="Voltar"
      actionButton={onClose}
      fullWidth
      dialogSize="md"
    >
      <LoadingFeedback
        open={isLoading || isCompanySupplierQuestionnaireUsersLoading}
      />
      <Box mb={2}>
        <Typography variant="subtitle1" className={classes.title}>
          Adicione os endereço de email e quais questionários os usuários irão
          responder.
        </Typography>
      </Box>
      <Box m={1} p={2}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="center"
          >
            <Grid item xs={12} md={5}>
              <Controller
                name="email"
                control={control}
                as={
                  <TextField
                    label="E-mail"
                    fullWidth
                    error={!!errors?.email}
                    helperText={errors?.email?.message}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Controller
                name="companySupplierQuestionnaires"
                control={control}
                onChange={([, data]) => data}
                as={
                  <Autocomplete
                    loading={isCompanySupplierQuestionnairesLoading}
                    getOptionLabel={(value) => value.questionnaireName}
                    getOptionSelected={(option, value) =>
                      !value || option.id === value.id || value.id === 0
                    }
                    options={companySupplierQuestionnaires || []}
                    textFieldProps={{
                      label: 'Questionários',
                      variant: 'outlined',
                      error: !!errors?.companySupplierQuestionnaires,
                      helperText:
                        errors?.companySupplierQuestionnaires?.message,
                    }}
                    multiple
                  />
                }
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                startIcon={<PlusCircleIcon />}
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
              >
                Adicionar
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
      <Box my={3}>
        <Typography variant="subtitle1" className={classes.title}>
          Usuários ativos:
        </Typography>
      </Box>
      <CompanySupplierAuditUserTable
        questionnaireUsers={questionnaireUsers}
        refresh={refresh}
        refreshQuestionnaires={refreshQuestionnaires}
        setIsLoading={setIsLoading}
      />
    </BaseDialog>
  )
}

export default UsersManagementDialog
